import React from 'react'
import { Container } from '@cleartrip/ct-design-components'
import { IBanner, LoginBanner } from '@/types/home'
import { useAppDispatch } from '@/redux/store/hooks'
import { setLineageId } from '@/redux/slices/lineageId'
import { getTestIdProps } from '@/utils/general/components'
import { PAGE_NAME } from '@/constants/pages/common'
import { HOME_PAGE_COMPONENTS_NAME } from '@/constants/components'

type BannerImage = IBanner | LoginBanner

interface IImageGridProps {
    images: BannerImage[]
    innerMargin?: number
    outterMargin?: number
    handleClick?: (url?: string) => void
    imageAlt?: string
}

function isFullWidthBanner(banner: BannerImage) {
    return banner.placholder?.toLowerCase() === 'fullwidth'
}

/** Split banners into rows: up to 4 per row; trailing full-width row when needed (e.g. 8 + 1 layout). */
function getBannerRows(images: BannerImage[]): BannerImage[][] {
    const fullWidthItems = images.filter(isFullWidthBanner)
    const gridItems = images.filter(banner => !isFullWidthBanner(banner))

    if (fullWidthItems.length === images.length) {
        return images.map(banner => [banner])
    }

    const rows: BannerImage[][] = []
    for (let i = 0; i < gridItems.length; i += 4) {
        rows.push(gridItems.slice(i, i + 4))
    }

    fullWidthItems.forEach(banner => rows.push([banner]))

    if (rows.length > 0) {
        return rows
    }

    if (images.length <= 4) {
        return [images]
    }

    if (images.length % 4 === 1) {
        const main = images.slice(0, -1)
        const splitRows: BannerImage[][] = []
        for (let i = 0; i < main.length; i += 4) {
            splitRows.push(main.slice(i, i + 4))
        }
        splitRows.push([images[images.length - 1]])
        return splitRows
    }

    const defaultRows: BannerImage[][] = []
    for (let i = 0; i < images.length; i += 4) {
        defaultRows.push(images.slice(i, i + 4))
    }
    return defaultRows
}

const ImageGrid = ({ images = [], innerMargin = 0, outterMargin = 0, handleClick, imageAlt }: IImageGridProps) => {
    const dispatch = useAppDispatch()

    const handleImageClick = (url?: string) => () => {
        dispatch(setLineageId())
        url && handleClick?.(url)
    }

    const rows = getBannerRows(images)

    if (!images.length) {
        return <></>
    }

    return (
        <Container
            css={{
                display: 'flex',
                flexDirection: 'column',
                gap: innerMargin,
                margin: outterMargin,
                width: '100%'
            }}
        >
            {rows.map((row, rowIndex) => {
                const numberOfImgs = row.length
                const imgWidth = `${(100 / numberOfImgs).toString()}%`

                return (
                    <Container css={{ display: 'flex', width: '100%' }} key={`banner-row-${rowIndex}`}>
                        {row.map((ele, idx) => (
                            <Container
                                css={{
                                    width: imgWidth,
                                    marginRight: idx < numberOfImgs - 1 ? innerMargin : 0,
                                    visibility: ele.src ? 'visible' : 'hidden'
                                }}
                                key={`${rowIndex}-${idx}`}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={ele.src}
                                    width="100%"
                                    onClick={handleImageClick(ele.url)}
                                    alt={`${imageAlt} ${rowIndex}-${idx}`}
                                    style={{ display: 'block', width: '100%' }}
                                    {...getTestIdProps({
                                        pageName: PAGE_NAME.HOME,
                                        componentName: HOME_PAGE_COMPONENTS_NAME.BSB_BANNER,
                                        testId: `${rowIndex}-${idx}`
                                    })}
                                />
                            </Container>
                        ))}
                    </Container>
                )
            })}
        </Container>
    )
}

export default ImageGrid
